import { useCallback, useState } from 'react';
import { shareSection } from '../../../../app/_shared/marketing/landingContent';
import { captureEvent, presenceEvents } from '../../presence/posthogPresence';
import './problemAndWhy.css';

/**
 * The page's only share affordance.
 *
 * Until this existed a reader who liked the page could do nothing but copy the URL out of the
 * address bar, so every post driving traffic here hit something that could not propagate.
 *
 * Shares the CURRENT path, not the homepage. A link passed on from /for-her keeps that door's
 * headline, description and share card — which is the point of having built per-door cards.
 *
 * Native share sheet where the browser has one (that is most phones, where sharing actually
 * happens), copy-to-clipboard everywhere else. No X/WhatsApp buttons: they need per-network SDKs or
 * ugly intent URLs, they date badly, and the native sheet already offers every app the person
 * actually uses.
 */
export function ShareBand() {
  const [copied, setCopied] = useState(false);
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const shareUrl = useCallback(() => {
    if (typeof window === 'undefined') return 'https://kynward.com';
    return `${window.location.origin}${window.location.pathname}`;
  }, []);

  const onShare = useCallback(async () => {
    const url = shareUrl();
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    if (canNativeShare) {
      try {
        await navigator.share({
          title: shareSection.shareTitle,
          text: shareSection.shareText,
          url,
        });
        captureEvent(presenceEvents.share_clicked, { method: 'native', path });
      } catch {
        // Dismissing the sheet rejects. That is a decision not to share, not a failure, so it gets
        // no error state and no event — counting it would inflate share intent.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      captureEvent(presenceEvents.share_clicked, { method: 'copy', path });
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [canNativeShare, shareUrl]);

  return (
    <section className="stmt-section share-band" aria-labelledby="share-heading">
      <header className="stmt-section__head">
        <h2 id="share-heading" className="ml-heading-serif ml-text-heading stmt-section__title">
          {shareSection.h2}
        </h2>
        <p className="stmt-section__lead">{shareSection.body}</p>
      </header>
      <div className="share-band__actions">
        <button type="button" className="share-band__btn" onClick={onShare}>
          {copied
            ? shareSection.copiedLabel
            : canNativeShare
              ? shareSection.shareCta
              : shareSection.copyCta}
        </button>
      </div>
    </section>
  );
}
